import React from "react";
import {
  AbsoluteFill,
  Easing,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { FrakLogo } from "../../components/FrakLogo";
import { accented, useCopy } from "../copy";
import { LOOK } from "../look";
import { useStage } from "../stage";
import { Words } from "../../b2c/ui/Words";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Scene 3 — the answer. The partner hero line, verbatim: "Frak transforme
 * vos clients en canal d'acquisition." Hard cut to Frak blue on the beat.
 * The customers from the partner hero (the same portraits, same network
 * idea) pop around the words and send a pulse inward along their links:
 * many people, one channel.
 */

const PEOPLE = [
  "center",
  "top-left",
  "top-mid",
  "top-right",
  "mid-left",
  "mid-right",
  "bottom-left",
  "bottom-right",
] as const;

/** Positions as fractions of the stage (x, y) + size, per shape. */
type Spot = { x: number; y: number; s: number };
const RING: Record<"wide" | "square" | "tall", Spot[]> = {
  wide: [
    { x: 0.08, y: 0.2, s: 150 },
    { x: 0.2, y: 0.8, s: 120 },
    { x: 0.05, y: 0.62, s: 100 },
    { x: 0.92, y: 0.2, s: 140 },
    { x: 0.8, y: 0.84, s: 120 },
    { x: 0.95, y: 0.6, s: 104 },
    { x: 0.3, y: 0.1, s: 92 },
    { x: 0.7, y: 0.1, s: 92 },
  ],
  square: [
    { x: 0.12, y: 0.12, s: 124 },
    { x: 0.5, y: 0.08, s: 96 },
    { x: 0.88, y: 0.13, s: 118 },
    { x: 0.08, y: 0.86, s: 110 },
    { x: 0.35, y: 0.92, s: 90 },
    { x: 0.66, y: 0.92, s: 96 },
    { x: 0.92, y: 0.85, s: 116 },
    { x: 0.94, y: 0.5, s: 0 },
  ],
  tall: [
    { x: 0.14, y: 0.1, s: 130 },
    { x: 0.5, y: 0.07, s: 100 },
    { x: 0.86, y: 0.11, s: 122 },
    { x: 0.1, y: 0.88, s: 116 },
    { x: 0.38, y: 0.93, s: 96 },
    { x: 0.66, y: 0.92, s: 100 },
    { x: 0.9, y: 0.87, s: 120 },
    { x: 0.94, y: 0.5, s: 0 },
  ],
};

export const Reveal: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const stage = useStage();
  const wide = stage.shape === "wide";

  const logo = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 200, mass: 0.7 },
  });
  const sub = spring({
    frame: frame - 34,
    fps,
    config: { damping: 20, stiffness: 180 },
  });
  const { text, accents } = accented(copy.reveal.rest, LOOK.white);
  const cx = stage.w / 2;
  const cy = stage.h / 2;

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(90% 90% at 50% 45%, ${LOOK.blue} 50%, ${LOOK.blueDeep} 100%)`,
        overflow: "hidden",
      }}
    >
      {/* Links + pulses from each customer to the centre. */}
      <svg
        width={stage.w}
        height={stage.h}
        style={{ position: "absolute", inset: 0 }}
      >
        {RING[stage.shape].map((spot, i) => {
          if (!spot.s) return null;
          const x = spot.x * stage.w;
          const y = spot.y * stage.h;
          const draw = interpolate(frame, [6 + i * 2, 22 + i * 2], [0, 1], {
            ...clamp,
            easing: Easing.bezier(0.16, 1, 0.3, 1),
          });
          // One pulse per link travelling inward, on a loop of ~1.2s.
          const cycle = ((frame - 24 - i * 5) % 36) / 36;
          const on = frame > 24 + i * 5;
          const px = x + (cx - x) * cycle;
          const py = y + (cy - y) * cycle;
          return (
            <g key={i}>
              <line
                x1={x}
                y1={y}
                x2={x + (cx - x) * draw}
                y2={y + (cy - y) * draw}
                stroke="rgba(255,255,255,0.22)"
                strokeWidth={3}
                strokeDasharray="2 12"
                strokeLinecap="round"
              />
              {on ? (
                <circle
                  cx={px}
                  cy={py}
                  r={9}
                  fill={LOOK.white}
                  opacity={0.9 * (1 - cycle)}
                />
              ) : null}
            </g>
          );
        })}
      </svg>

      {/* Soft halo behind the text so the links fade out under it. */}
      <div
        style={{
          position: "absolute",
          left: cx - (wide ? 820 : 520),
          top: cy - (wide ? 300 : 360),
          width: wide ? 1640 : 1040,
          height: wide ? 600 : 720,
          borderRadius: "50%",
          background: `radial-gradient(closest-side, ${LOOK.blue} 60%, rgba(0,67,239,0))`,
        }}
      />

      {RING[stage.shape].map((spot, i) => {
        if (!spot.s) return null;
        const p = spring({
          frame: frame - 2 - i * 2,
          fps,
          config: { damping: 11, stiffness: 220, mass: 0.6 },
        });
        const bob = Math.sin((frame + i * 13) / 18) * 6;
        return (
          <div
            key={PEOPLE[i]}
            style={{
              position: "absolute",
              left: spot.x * stage.w - spot.s / 2,
              top: spot.y * stage.h - spot.s / 2 + bob,
              width: spot.s,
              height: spot.s,
              borderRadius: spot.s,
              overflow: "hidden",
              border: `5px solid ${LOOK.white}`,
              boxShadow: "0 16px 32px -12px rgba(0, 20, 80, 0.6)",
              scale: String(p),
            }}
          >
            <Img
              src={staticFile(`b2b/people/${PEOPLE[i]}.webp`)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                scale: "1.1",
              }}
            />
          </div>
        );
      })}

      <AbsoluteFill
        style={{
          alignItems: "center",
          justifyContent: "center",
          padding: wide ? "0 300px" : "0 90px",
          textAlign: "center",
        }}
      >
        <div style={{ scale: logo, marginBottom: 34 }}>
          <FrakLogo
            width={wide ? 300 : 260}
            style={{ filter: "brightness(0) invert(1)" }}
          />
        </div>
        <Words
          text={text}
          start={6}
          stagger={3}
          fontSize={wide ? 92 : 84}
          color={LOOK.sky}
          accents={accents}
          lineHeight={1.04}
        />
        <div
          style={{
            marginTop: 34,
            fontFamily: LOOK.body,
            fontWeight: LOOK.medium,
            fontSize: 40,
            color: LOOK.sky,
            opacity: sub,
            translate: `0 ${(1 - sub) * 20}px`,
            textWrap: "balance",
          }}
        >
          {copy.reveal.sub}
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};
